import { datadogRum } from '@datadog/browser-rum'
import { ISliceDefault, LoadersType } from '../interfaces'
import {
  addLoader,
  removeLoader,
  setLoadingDelay,
} from '../redux/slices/config'
import { AppDispatch, RootState } from '../redux/store'
import { ESCAPES_ORIGIN } from './checkoutRuleRoute'
import { generateDataDogContext } from './dataDog'
import getUrlParams from './getUrlParams'
import { redactCredentialParams } from './redactRumUrls'
import { hasPendingDebouncedCalls } from './debounce'
import {
  giveUpOnPresumedHungWrites,
  settlePendingCartWrites,
} from './pendingCartWrites'

export interface ReloadPageArgs {
  /**
   * Route to load instead of the current one: absolute, without the basename,
   * like `/{{params.slug}}/{{params.sessionId}}`.
   */
  path?: unknown
}

const FUNCTION_NAME = 'reloadPage'

/**
 * The reload waiting for cart writes, if one is: one per page (COM-1521).
 * Set when the wait starts and cleared once the page load has been started.
 */
let waitingReload: Promise<void> | undefined

/**
 * When a page load was started in this document, or `undefined`: the time
 * the navigation call in `loadPage` returned without throwing. The page is
 * on its way out, so a later `reloadPage` does nothing (COM-1551). A load
 * that threw does not set it. See `pageLoadUnderway` for how long it holds.
 */
let pageLoadStartedAt: number | undefined

/**
 * How long a page load started by `reloadPage` blocks the next `reloadPage`
 * to the same URL in the same session (COM-1499). A shopper's tap lets one
 * reload through it per window, not every one (COM-1551): see `reloadPage`
 * and `checkLoopLatch`.
 */
export const RELOAD_PAGE_LOOP_WINDOW_MS = 10000

/**
 * Whether a page load started in this document is still on its way. A
 * navigation call can return and still not navigate (a `204` answer, or a
 * webview that swallows it), so the page would stay with every `reloadPage`
 * dead. So it holds for `RELOAD_PAGE_LOOP_WINDOW_MS` only: a page still
 * alive that long after the call returned did not navigate, and later
 * reloads go ahead again, still under the loop latch. Read with the clock
 * the latch uses, at call time, so there is no timer to clean up. A start
 * time after now (a clock set back) reads as not underway, as the latch
 * reads a stamp in the future.
 */
const pageLoadUnderway = (): boolean => {
  if (pageLoadStartedAt === undefined) {
    return false
  }
  const elapsedMs = Date.now() - pageLoadStartedAt
  if (elapsedMs >= 0 && elapsedMs < RELOAD_PAGE_LOOP_WINDOW_MS) {
    return true
  }
  pageLoadStartedAt = undefined
  return false
}

/**
 * Whether this page load has already reported that the loop latch cannot use
 * `sessionStorage`. One report per page load: storage that is blocked stays
 * blocked, and every `reloadPage` would otherwise report it again.
 */
let latchStorageFailureReported = false

/**
 * Forgets everything this module holds for the current page load: a reload
 * left waiting, a page load already started, and the latch storage report.
 * `sessionStorage` is left alone,
 * as a real page load leaves it, so a test calls this to stand for the next
 * page load.
 * @internal
 */
export const resetWaitingReload = () => {
  waitingReload = undefined
  pageLoadStartedAt = undefined
  latchStorageFailureReported = false
}

/** The URL the router would show for `path`, with its basename in front. */
const withBaseName = (path: string): string => {
  const { baseName } = getUrlParams(window.location.pathname)
  if (!baseName || baseName === '/') {
    return path
  }

  return `/${baseName}${path}`
}

/**
 * The destination as the browser will parse it, or `null` when it would leave
 * this origin or cannot be parsed. The raw-string `ESCAPES_ORIGIN` test is not
 * enough on its own: URL parsing strips ASCII tab, LF and CR, so
 * `/\t/evil.com` passes the regex and still loads `//evil.com`. And the path
 * is not only trusted config: `RouterParams` merges query params over route
 * params, so `?slug=%09%2Fevil.com` reaches `{{params.slug}}`.
 */
const sameOriginDestination = (path: string): string | null => {
  try {
    const url = new URL(withBaseName(path), window.location.origin)
    return url.origin === window.location.origin ? url.href : null
  } catch {
    return null
  }
}

/**
 * Whether loading `destination` would stay in this document: it carries a
 * fragment and is the current URL but for that fragment. The browser then
 * only scrolls, so `location.replace` would load nothing, and after a wait the
 * `RELOAD_PAGE` splash would stay up for good (COM-1521). The same URL with
 * no fragment is a real load and is not caught here.
 */
const staysInDocument = (destination: string): boolean => {
  const hasFragment = destination.includes('#')
  if (!hasFragment) {
    return false
  }
  const target = new URL(destination)
  const { origin, pathname, search } = window.location
  const isSameUrl =
    target.origin === origin &&
    target.pathname === pathname &&
    target.search === search
  return isSameUrl
}

/**
 * What a page load for `path` does: reload the current URL, reload it after
 * reporting a path it cannot follow, or replace it with `destination`.
 */
type PageLoad =
  | { kind: 'reload' }
  | { kind: 'rejected' }
  | { kind: 'replace'; destination: string }

/** Decides the page load for `path` without starting it. */
const resolvePageLoad = (path: unknown): PageLoad => {
  if (path === undefined) {
    return { kind: 'reload' }
  }

  const destination =
    typeof path === 'string' &&
    path.startsWith('/') &&
    !ESCAPES_ORIGIN.test(path)
      ? sameOriginDestination(path)
      : null
  if (!destination) {
    return { kind: 'rejected' }
  }

  if (staysInDocument(destination)) {
    return { kind: 'reload' }
  }

  return { kind: 'replace', destination }
}

/**
 * The current pathname, or `''`. Never absent in a browser; a `location`
 * stub in a test may carry no `pathname`, and building the latch key must not
 * throw there. A blocked call or a storage-failure report can still throw
 * with such a stub, from `generateDataDogContext`.
 */
const currentPathname = (): string => window.location.pathname || ''

/**
 * The URL `load` would load, as pathname and query: the current one for a
 * reload, the destination for a replace. No origin, since `sessionStorage` is
 * per origin already, and no fragment, since a fragment does not change what
 * loads.
 */
const loadedUrl = (load: PageLoad): string => {
  if (load.kind === 'replace') {
    const { pathname, search } = new URL(load.destination)
    return `${pathname}${search}`
  }

  return `${currentPathname()}${window.location.search || ''}`
}

/**
 * The `sessionStorage` key of the loop latch for `load`. It carries the
 * session id although the URL usually does too, so a route that puts the id
 * elsewhere, or not at all, still keys per session. The id is the positional
 * one `getUrlParams` reads, which knows the ephemeral commit SHA and the
 * language prefix (`generateDataDogContext`'s own split does not), and is the
 * session the page doing the reload is in, not the destination's.
 */
const loopLatchKey = (load: PageLoad): string => {
  const { sessionId } = getUrlParams(currentPathname())
  return `cinnamon.reloadPage.${sessionId || ''}.${loadedUrl(load)}`
}

type LatchStorageStep = 'sessionStorage' | 'getItem' | 'setItem'

/**
 * Reports, once per page load, that the loop latch is off because
 * `sessionStorage` failed at `step`.
 */
const reportLatchStorageFailure = (step: LatchStorageStep, error: unknown) => {
  if (latchStorageFailureReported) {
    return
  }
  latchStorageFailureReported = true
  const context = generateDataDogContext({
    title: 'reloadPage loop latch is off: sessionStorage failed',
    extraInfo: { function: FUNCTION_NAME, step },
  })
  datadogRum.startView(context.viewName)
  datadogRum.addError(error, context)
}

/**
 * `sessionStorage`, or `undefined` when it cannot be used. The getter itself
 * throws where storage is blocked (a `SecurityError`), and an Android webview
 * with DOM storage turned off answers `null`.
 */
const latchStorage = (): Storage | undefined => {
  try {
    const storage = window.sessionStorage
    if (!storage) {
      reportLatchStorageFailure(
        'sessionStorage',
        new Error('sessionStorage is not available'),
      )
      return undefined
    }
    return storage
  } catch (error) {
    reportLatchStorageFailure('sessionStorage', error)
    return undefined
  }
}

/**
 * Marks a stored latch value as an activation skip: a load that went ahead
 * inside the window only because the shopper had tapped (COM-1551). The
 * value is then `<ms>:skip`; any other load is stored as the bare `<ms>`,
 * the format COM-1499 wrote, so a value already in `sessionStorage` when
 * this ships reads as it did, as a load that was not a skip. A suffix rather
 * than JSON keeps that one format for both builds: a build from before this
 * one reads `<ms>:skip` as not a number, so as no recent load, which lets a
 * reload through rather than holding one off.
 */
const ACTIVATION_SKIP_SUFFIX = ':skip'

/** What the loop latch recorded about the last page load for a key. */
interface LoopLatchEntry {
  /** When that load started. */
  at: number
  /** Whether that load was let through the window only by a tap. */
  activationSkip: boolean
}

/**
 * The load a stored latch value records, or `undefined` for none. A value
 * that is not a number, or that lies after `now`, reads as no recent reload
 * rather than as blocked storage: corrupted, or written under a clock since
 * set back, it must not hold reloads off. `NaN` and `Infinity` both fail the
 * one comparison; `-Infinity` passes it and reads as long ago, which is the
 * same answer. The `:skip` suffix is read off first.
 */
const parseLatchEntry = (
  stored: string | null,
  now: number,
): LoopLatchEntry | undefined => {
  if (stored === null) {
    return undefined
  }
  const activationSkip = stored.endsWith(ACTIVATION_SKIP_SUFFIX)
  const time = activationSkip
    ? stored.slice(0, -ACTIVATION_SKIP_SUFFIX.length)
    : stored
  const at = Number(time)
  const isPlausible = at <= now
  return isPlausible ? { at, activationSkip } : undefined
}

/** The last page load for `key`, or `undefined`. */
const readLoopLatch = (
  key: string,
  now: number,
): LoopLatchEntry | undefined => {
  const storage = latchStorage()
  if (!storage) {
    return undefined
  }
  try {
    return parseLatchEntry(storage.getItem(key), now)
  } catch (error) {
    reportLatchStorageFailure('getItem', error)
    return undefined
  }
}

/**
 * Records that a page load for `key` has just started, and whether it was an
 * activation skip. One entry per key.
 */
const stampLoopLatch = (key: string, activationSkip: boolean) => {
  const storage = latchStorage()
  if (!storage) {
    return
  }
  try {
    const suffix = activationSkip ? ACTIVATION_SKIP_SUFFIX : ''
    storage.setItem(key, `${Date.now()}${suffix}`)
  } catch (error) {
    reportLatchStorageFailure('setItem', error)
  }
}

/**
 * The browser's user activation state for this page load, or `undefined`
 * where it has no `navigator.userActivation` (iOS before 16.4, Android
 * WebView before 72, jsdom). The DOM types declare it always present; it is
 * not.
 */
const readUserActivation = (): UserActivation | undefined =>
  (window.navigator as Partial<Navigator>).userActivation

/**
 * Whether the shopper has tapped, clicked or pressed a key in this page load:
 * the browser's sticky user activation (COM-1551). A scroll does not count,
 * and neither does an event a script dispatches. It stays set for the rest of
 * the page load, so a reload that a tap led to counts however it arrived: a
 * button, a `runStep` outcome the poller answers later, the place-order
 * queue, an after-order handler. `false` where the browser cannot say, so
 * there the loop latch applies to every reload, as before COM-1551.
 */
const userActivated = (): boolean =>
  readUserActivation()?.hasBeenActive === true

/** The action name, and title, of the activation skip report. */
const ACTIVATION_SKIP_ACTION = 'reloadPage loop latch skipped by activation'

/**
 * The Datadog context of a decision of the loop latch: the same fields for
 * the loop report (an error) and the skip report (an action).
 */
const latchDecisionContext = ({
  title,
  args,
  key,
  elapsedMs,
  extraInfo,
}: {
  title: string
  args?: ReloadPageArgs
  key: string
  elapsedMs: number
  extraInfo?: Record<string, unknown>
}) =>
  generateDataDogContext({
    title,
    extraInfo: {
      function: FUNCTION_NAME,
      path: redactCredentialParams(String(args?.path)),
      latchKey: redactCredentialParams(key),
      elapsedMs,
      windowMs: RELOAD_PAGE_LOOP_WINDOW_MS,
      ...extraInfo,
    },
  })

/**
 * Sends the activation skip as a RUM action, not an error: the reload went
 * ahead and nothing failed. `startView` first, as every report in this file
 * and `useReorder`'s action do: where `REACT_APP_DATADOG_ENABLE_TRACKING_VIEW`
 * turns on `trackViewsManually`, the SDK holds no view until one is started,
 * and an event needs a view to be kept.
 */
const reportActivationSkip = (
  args: ReloadPageArgs | undefined,
  key: string,
  elapsedMs: number,
) => {
  const context = latchDecisionContext({
    title: ACTIVATION_SKIP_ACTION,
    args,
    key,
    elapsedMs,
  })
  datadogRum.startView(context.viewName)
  datadogRum.addAction(ACTIVATION_SKIP_ACTION, context)
}

/**
 * What the loop latch says about a reload:
 * - `load`: nothing loaded the URL inside the window; the load is stamped as
 *   an ordinary one.
 * - `skip`: something did, but the shopper has tapped and the last load was
 *   not itself a skip; the load goes ahead and is stamped as a skip.
 *   `reportSkip` sends the RUM action `reloadPage loop latch skipped by
 *   activation`; the
 *   caller sends it for the check whose answer it acts on, so a reload that
 *   waits sends it once, from the check after the wait.
 * - `block`: nothing loads, and `reloadPage loop prevented` has been sent.
 */
type LoopLatchCheck =
  | { kind: 'load' }
  | { kind: 'skip'; reportSkip: () => void }
  | { kind: 'block' }

/**
 * The loop latch, with the activation rule in it (COM-1499, COM-1551): the
 * one check both `reloadPage` and `loadAfterWait` run. A reload that would
 * load what a page load started less than `RELOAD_PAGE_LOOP_WINDOW_MS` ago
 * already loaded, in this session, is blocked unless the shopper has tapped
 * in this page load (`userActivated`) and that last load was not itself let
 * through by a tap. So a tap lets one reload through per window: a loop
 * that a shopper taps through on every boot ends after one extra round, and
 * a shopper's third reload within 10 s of their second is blocked.
 *
 * The reported `path` and `latchKey` carry a destination's query, so
 * credential parameters are redacted in them as `beforeSend` redacts them in
 * URLs; the stored key is not. The block report's `userActivationSupported`
 * says whether the browser could tell a shopper's tap from a loop, that is
 * whether it answers `hasBeenActive` with a boolean: where it is `false` (no
 * `navigator.userActivation`, or one that answers anything else) the latch
 * also blocks a shopper repeating an action, so those reports are not all
 * loops. Its `activationSkipSpent` is `true` where the shopper had tapped
 * and the block is only because the last load already used the skip.
 *
 * It reads the URL, the clock and the activation when it is called, so the
 * check after a wait judges what is about to load (`loadAfterWait`).
 */
const checkLoopLatch = (args?: ReloadPageArgs): LoopLatchCheck => {
  const key = loopLatchKey(resolvePageLoad(args?.path))
  const now = Date.now()
  const lastLoad = readLoopLatch(key, now)
  if (lastLoad === undefined) {
    return { kind: 'load' }
  }
  const elapsedMs = now - lastLoad.at
  if (elapsedMs >= RELOAD_PAGE_LOOP_WINDOW_MS) {
    return { kind: 'load' }
  }

  const activated = userActivated()
  if (activated && !lastLoad.activationSkip) {
    const reportSkip = () => reportActivationSkip(args, key, elapsedMs)
    return { kind: 'skip', reportSkip }
  }

  const context = latchDecisionContext({
    title: 'reloadPage loop prevented',
    args,
    key,
    elapsedMs,
    extraInfo: {
      userActivationSupported:
        typeof readUserActivation()?.hasBeenActive === 'boolean',
      activationSkipSpent: activated,
    },
  })
  datadogRum.startView(context.viewName)
  datadogRum.addError(new Error('reloadPage loop prevented'), context)
  return { kind: 'block' }
}

/**
 * The reload itself: see `reloadPage`. `latch` is what the loop latch said
 * about it; a `skip` is reported and stamped as one here, once the load has
 * started. The loop latch is stamped once the
 * navigation call has returned, keyed on the URL loaded now, which after a
 * wait need not be the one the call began on (a `pushAfter` can land during
 * it). A navigation call that throws started nothing, so it stamps nothing
 * and a later reload is not held off. The page has not gone yet when the call
 * returns: navigation is asynchronous, and the write is synchronous.
 */
const loadPage = (args: ReloadPageArgs | undefined, latch: LoopLatchCheck) => {
  const path = args?.path
  const load = resolvePageLoad(path)
  if (load.kind === 'rejected') {
    const context = generateDataDogContext({
      title: 'reloadPage was given a path it cannot follow',
      extraInfo: { function: FUNCTION_NAME, path: String(path) },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error('reloadPage path is not an in-origin absolute path'),
      context,
    )
  }

  const latchKey = loopLatchKey(load)
  if (load.kind === 'replace') {
    // snyk:ignore:javascript/OR -- FP: destination comes from sameOriginDestination, which returns null unless the parsed URL origin equals window.location.origin
    window.location.replace(load.destination)
  } else {
    window.location.reload()
  }
  pageLoadStartedAt = Date.now()
  if (latch.kind === 'skip') {
    latch.reportSkip()
  }
  stampLoopLatch(latchKey, latch.kind === 'skip')
}

/**
 * The splash state as it is now (`defaultSlice`), read through the store's
 * thunk middleware, since this util is handed `dispatch` and not the store.
 * `undefined` where no store answers (a `dispatch` stub in a test).
 */
const readSplashState = (
  dispatch: AppDispatch,
):
  | Pick<ISliceDefault, 'loaders' | 'loadingDelay' | 'loadingDelayOwner'>
  | undefined => {
  const slice: unknown = dispatch(
    (_ignored: AppDispatch, getState: () => RootState) =>
      getState().defaultSlice,
  )
  if (!slice || typeof slice !== 'object') {
    return undefined
  }
  return slice as ISliceDefault
}

/**
 * Whether another loader raised with the object form wrote the splash
 * debounce after this wait did and is still up (COM-1581).
 */
const delayHeldByAnotherLoader = ({
  loaders,
  loadingDelayOwner,
}: Pick<ISliceDefault, 'loaders' | 'loadingDelayOwner'>) =>
  loadingDelayOwner !== undefined &&
  loadingDelayOwner !== LoadersType.RELOAD_PAGE &&
  loaders.includes(loadingDelayOwner)

/**
 * Lowers the wait's `RELOAD_PAGE` splash, for a wait that ends with the page
 * still here, and puts the splash debounce it set to 0 back to
 * `loadingDelayBefore`, unless another loader raised with the object form
 * during the wait wrote it and is still up (COM-1581). That is
 * `CREATE_ORDER` (`useCreateOrder`) today: putting the old value back would
 * hold its splash off while the order is still being created, so the
 * debounce is left as it set it. Once that loader has been lowered, the
 * debounce is put back as when no other loader rose. The splash is lowered in
 * every case.
 */
const lowerWaitSplash = (
  dispatch?: AppDispatch,
  loadingDelayBefore?: number,
) => {
  const splash = dispatch ? readSplashState(dispatch) : undefined
  const shouldRestoreLoadingDelay =
    !!splash &&
    loadingDelayBefore !== undefined &&
    !delayHeldByAnotherLoader(splash)
  if (dispatch && shouldRestoreLoadingDelay) {
    dispatch(setLoadingDelay(loadingDelayBefore))
  }
  dispatch?.(removeLoader(LoadersType.RELOAD_PAGE))
}

/**
 * Starts the page load once the wait is over. The `waitingReload` latch is
 * cleared either way; the splash and the 0 debounce are left once the load
 * has started, because the page goes with them. The splash is lowered and
 * the debounce put back, unless another loader wrote it during the wait
 * and is still up (`lowerWaitSplash`), with the page under them otherwise dead for good,
 * when no load starts:
 * - The loop latch blocks it (COM-1551). It is checked again here
 *   (`checkLoopLatch`, the same rule as at call time), on the URL about to
 *   load, the time now and the activation now, since a `pushAfter` that lands
 *   during the wait can move the URL to one loaded inside the window; the
 *   check `reloadPage` ran when the call came in saw the URL before it.
 *   Reported as `reloadPage loop prevented`. This check is the one whose
 *   answer the load acts on, so an activation skip is reported from here, not
 *   from the call.
 *   Live versus defensive: a wait starts only with an add or remove pending
 *   in `debounceById` (`useAddItemToCart`, `useRemoveItemFromCart`), which a
 *   tap normally starts, and a tapped page is let through once per window.
 *   So where `navigator.userActivation` answers, this block fires only when
 *   no tap started the write (an authored action that adds or removes on its
 *   own) or the skip was already spent; it is mostly defensive there. It is
 *   live where the browser has no `navigator.userActivation`, or answers
 *   `hasBeenActive` with anything but `true`.
 * - The load throws, which starts no navigation. The throw is reported as
 *   `reloadPage could not start the page load`. A throw from the latch check
 *   itself lands in the same catch and is reported the same way; that needs
 *   a `location` stub with no `pathname` (`generateDataDogContext` reads it),
 *   so it is not reachable in a browser.
 */
const loadAfterWait = (
  args?: ReloadPageArgs,
  dispatch?: AppDispatch,
  loadingDelayBefore?: number,
) => {
  try {
    const latch = checkLoopLatch(args)
    if (latch.kind === 'block') {
      lowerWaitSplash(dispatch, loadingDelayBefore)
      return
    }
    loadPage(args, latch)
  } catch (error) {
    lowerWaitSplash(dispatch, loadingDelayBefore)
    const context = generateDataDogContext({
      title: 'reloadPage could not start the page load',
      extraInfo: { function: FUNCTION_NAME },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  } finally {
    waitingReload = undefined
  }
}

/**
 * Reloads the page. The handler behind `TemplateActionNames.RELOAD_PAGE`
 * (COM-1496).
 *
 * With no `path`, it reloads the current URL. With one, it loads that route
 * with `location.replace`: a full page load, so every boot read runs again.
 *
 * Why not the router: a `pushView` action followed by a reload does nothing,
 * because the navigation swaps the screen and the reload is then emitted to
 * the new screen's `EventManager` before it registers its handlers. Calling
 * `navigate` inside this handler would also render the target screen, and
 * run its mount effects, before the page unloads, and would leave the old
 * screen in history. `location.replace` does neither.
 *
 * A path that is the current URL but for its fragment reloads the current
 * page instead, without the new fragment: `location.replace` would only
 * scroll (COM-1521).
 *
 * A path that escapes the origin is not followed: one that `ESCAPES_ORIGIN`
 * rejects (`//host`, `/\host`, the decoded `%2f` case), one whose parsed
 * destination has another origin (tab, LF or CR between the slashes), one
 * that is not an absolute string, or one the URL parser rejects. It is
 * reported to Datadog and the current page reloads instead.
 * An unresolved whole-value template arrives as `undefined` and so reads as
 * no `path` at all; that case cannot be told apart and is not reported.
 *
 * Add and remove writes are not lost (COM-1500). A quantity change still
 * waiting in the add or remove debounce is sent first, and the page loads only
 * once it and any add or remove already sent through that debounce have
 * answered, since unloading the page aborts a request. That includes ones the
 * shopper queues while it waits: the wait drains the debounce until nothing is
 * waiting or in flight (`flushDebouncedCalls`). Only those are tracked
 * (`debounceById`): a delete-product, clear-cart or prefill write in flight is
 * not waited for (COM-1512). The whole wait is bounded once by
 * `PENDING_CART_WRITES_WAIT_MS`, so a request that never answers, or taps
 * that keep coming, delay the reload but cannot stop it. Before it decides
 * whether to wait, it gives up on, and reports, writes in flight for
 * `PRESUMED_HUNG_CART_WRITE_MS` (15 s, `giveUpOnPresumedHungWrites`), so a
 * write presumed hung neither makes it wait nor raises the splash; a slow
 * write younger than that is still waited for (COM-1521). A pending write
 * that throws before it is sent is reported and dropped, and the page still
 * loads (PR #850).
 *
 * With nothing pending the page loads at once, synchronously, as before: no
 * splash, no promise. Otherwise, during the wait (COM-1521):
 * - It raises the `RELOAD_PAGE` loader, with no splash delay, so `Splash`
 *   covers the screen and the shopper cannot confirm, delete or navigate in
 *   a page that is about to go. `dispatch` is what raises it; `EventManager`
 *   passes its own, and without one no splash goes up. The object form
 *   writes the global `loadingDelay` to 0, as `CREATE_ORDER` does. The
 *   loader and the 0 are left once the load starts, since the page goes;
 *   if starting it throws, the loader is lowered and `loadingDelay` is put
 *   back to the value read before raising it (`loadAfterWait`). The same
 *   happens if the loop latch holds the load off after the wait. In both
 *   cases the value is put back only if no other loader's object form, such
 *   as `CREATE_ORDER`'s, wrote `loadingDelay` during the wait; otherwise it
 *   is left as that loader set it (`lowerWaitSplash`, COM-1581).
 * - It latches: a `reloadPage` emitted while one waits starts no second
 *   drain, raises nothing and loads nothing, and returns the waiting one's
 *   promise. Its own `path` is ignored: the first reload's destination
 *   stands.
 * The promise never rejects, and nothing awaits it.
 *
 * It holds off a reload loop (COM-1499). Authored on a screen's `runOnLoad`
 * behind an outcome that recurs on every load, such as a step that always
 * answers `repriced`, each load would reload again, and the webview would
 * spin with no exit and nothing reported. So a page load it starts is
 * recorded in `sessionStorage`, which survives the load where module state
 * (`waitingReload`) does not, under one key per session id and URL loaded
 * (`loopLatchKey`): the current URL for no `path`, for a path it cannot
 * follow and for the fragment-only case, the destination otherwise. A
 * `reloadPage` that would load a URL whose last load started less than
 * `RELOAD_PAGE_LOOP_WINDOW_MS` (10 s) ago loads nothing. Blocked at call time
 * it raises no splash, starts no drain, runs no
 * `giveUpOnPresumedHungWrites` and navigates nowhere; it returns `undefined`
 * and reports `reloadPage loop prevented` to Datadog, once per blocked call,
 * with the key and the time since that load. Blocked at load time, after a
 * wait (below), the splash has already been raised and the drain run: the
 * splash is lowered, the promise resolves, and the block is reported once,
 * for the reload that started the wait; a reload joined to that wait is not
 * reported separately. The checks run in this order: a reload already
 * waiting is joined first, then a page load already started, then the
 * latch, then the rest.
 * - Once a page load has started in this document (the navigation call in
 *   `loadPage` returned), a later `reloadPage` does nothing: no second
 *   navigation, no splash, no drain, no report, `undefined` (COM-1551). The
 *   page is already going. A load that threw does not count. It holds for
 *   `RELOAD_PAGE_LOOP_WINDOW_MS` after the call returned: a page still alive
 *   then did not navigate, and reloads go ahead again, under the latch
 *   (`pageLoadUnderway`).
 * - A shopper's tap lets one reload through the window (COM-1551). The
 *   browser's sticky user activation is set once the shopper has tapped,
 *   clicked or pressed a key in this page load (`userActivated`). With it
 *   set, a reload the latch would block goes ahead, is stamped as an
 *   activation skip, and is sent as the RUM action `reloadPage loop latch
 *   skipped by activation` (an action, not an error: nothing failed), unless the last load of that URL was itself a skip; then
 *   it is blocked and reported as a loop with `activationSkipSpent: true`.
 *   A reload the latch would not block is stamped as an ordinary load,
 *   tapped or not. So a `runOnLoad` loop with no interaction between boots
 *   is blocked as in COM-1499; a shopper's retry on a page that boots in
 *   2 s reloads; their third reload within 10 s of the second is blocked.
 *   The latch is stamped on every load, so the next boot without
 *   interaction is held off.
 * - Why the browser's state and not the trigger: every action is emitted
 *   from `createFunction` (`utils/processActions.ts`), but a reload also
 *   arrives from a `runStep`'s deferred handlers through `runCinnamonActions`
 *   (`hooks/workflow/useWorkflowStep.ts`), from after-order handlers
 *   (`hooks/orders/useOrderConfirmation.ts`) and from the place-order queue,
 *   so a trigger would have to cross the `EventEmiter` signature and the
 *   poller. Activation covers each of them, since each starts from a tap.
 * - ⚠️ A loop the shopper taps through on every boot gets one extra round,
 *   not one per boot: the skip is spent by the first and the next is
 *   blocked. The activation lasts the whole page load, not one screen, so a
 *   shopper who tapped on an earlier screen and reached a looping screen by
 *   a client-side `pushView` (`navigate`, the same page load) uses the skip
 *   that way. Where the browser has no `navigator.userActivation` (iOS
 *   before 16.4) the latch applies to every reload, as in COM-1499, and
 *   blocks a shopper repeating an action inside 10 s too; the report's
 *   `userActivationSupported` tells those reports apart.
 * - After a wait the latch is checked again (`loadAfterWait`), on the URL
 *   about to load, the time the load starts and the activation then, under
 *   the same rule: a `pushAfter` that moved the URL during the wait to one
 *   loaded inside the window is held off unless a tap's skip lets it
 *   through. The splash is then lowered and `loadingDelay` put back (unless
 *   another loader wrote it during the wait and is still up), as when starting the load
 *   throws. Where `navigator.userActivation` answers this
 *   is mostly defensive, since a wait is normally started by a tap; see
 *   `loadAfterWait`.
 * - The window opens when the load starts (`loadPage`), not when the call
 *   came in, so a reload that waited up to `PENDING_CART_WRITES_WAIT_MS` for
 *   cart writes does not open it early. A load that throws opens none.
 * - The clock is `Date.now()`, not `performance.now()`, which restarts with
 *   every page load.
 * - ⚠️ It slows a loop, it does not end it: a page that takes longer than
 *   10 s from the reload to the next `reloadPage` passes the latch every time
 *   and still loops, one load per boot. A loop between two URLs is caught,
 *   since each keeps its own entry, if one round is under 10 s; one that
 *   moves the URL every time, such as a counter in the query, is not.
 * - Where `sessionStorage` cannot be used (the getter throws, it is `null`,
 *   or `getItem` or `setItem` throws) the latch is off for that call, the
 *   reload goes ahead, nothing throws, and that is reported once per page
 *   load. A stored value that is not a number, or lies in the future, reads
 *   as no recent load. A skip is stored as `<ms>:skip`, an ordinary load as
 *   the bare `<ms>` COM-1499 wrote (`ACTIVATION_SKIP_SUFFIX`).
 * @internal
 */
export const reloadPage = (
  args?: ReloadPageArgs,
  dispatch?: AppDispatch,
): void | Promise<void> => {
  if (waitingReload) {
    return waitingReload
  }
  if (pageLoadUnderway()) {
    return undefined
  }
  const latch = checkLoopLatch(args)
  if (latch.kind === 'block') {
    return undefined
  }
  giveUpOnPresumedHungWrites(FUNCTION_NAME)
  if (!hasPendingDebouncedCalls()) {
    loadPage(args, latch)
    return undefined
  }
  const splashBefore = dispatch ? readSplashState(dispatch) : undefined
  const loadingDelayBefore =
    typeof splashBefore?.loadingDelay === 'number'
      ? splashBefore.loadingDelay
      : undefined
  dispatch?.(addLoader({ name: LoadersType.RELOAD_PAGE, delay: 0 }))
  const reload = settlePendingCartWrites(FUNCTION_NAME).then(() =>
    loadAfterWait(args, dispatch, loadingDelayBefore),
  )
  waitingReload = reload
  return reload
}

export default reloadPage
